// frontend/src/pages/LoginPage.js (Enhanced with return URL support)
import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";

const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Validate return URL to prevent open redirect attacks
  const validateReturnUrl = (url) => {
    if (!url) return '/';
    // Only allow relative URLs that start with /
    if (!url.startsWith('/')) return '/';
    // Prevent protocol-relative URLs
    if (url.startsWith('//')) return '/';
    // Prevent javascript: or data: URLs
    if (url.match(/^(javascript|data):/i)) return '/';
    return url;
  };
  
  // Get return URL from location state or query params
  const rawReturnUrl = location.state?.from || 
                       new URLSearchParams(location.search).get('returnUrl');
  const from = validateReturnUrl(rawReturnUrl) || '/';

  useEffect(() => {
    if (isAuthenticated) {
      // If already authenticated, redirect to intended destination
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const result = await login(username, password);

    if (result.success) {
      // Navigate to the original requested page after successful login
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-md w-full space-y-8">
        <div>
          <Link
            to="/"
            className="flex items-center text-[#00A3E0] hover:text-[#002855] mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t({ en: "Back to Academy", de: "Zurück zur Academy" })}
          </Link>

          <h2 className="mt-6 text-center text-3xl font-extrabold text-[#002855]">
            {t({
              en: "Login to burnair Academy",
              de: "Bei burnair Academy anmelden",
            })}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-500">
            {t({
              en: "Use your burnair Map login credentials",
              de: "Verwende deine burnair Map Zugangsdaten",
            })}
          </p>
        </div>

        <div className="bg-white p-8 rounded-lg shadow-md border border-gray-200">
          {/* Form with proper attributes for password managers */}
          <form onSubmit={handleSubmit} autoComplete="on" className="space-y-6">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-[#002855] mb-1"
              >
                {t({ en: "Email", de: "E-Mail" })}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00A3E0] focus:border-[#00A3E0] transition-colors"
                placeholder={t({
                  en: "Enter your email address",
                  de: "E-Mail-Adresse eingeben",
                })}
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-[#002855] mb-1"
              >
                {t({ en: "Password", de: "Passwort" })}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00A3E0] focus:border-[#00A3E0] transition-colors"
                placeholder={t({
                  en: "Enter your password",
                  de: "Passwort eingeben",
                })}
              />
            </div>

            {error && (
              <div className="text-[#DA291C] text-sm bg-red-50 border border-red-200 p-3 rounded-md">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !username || !password}
              className="w-full bg-[#002855] text-white py-2 px-4 rounded-md hover:bg-[#001a3d] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading
                ? t({ en: "Logging in...", de: "Anmeldung läuft..." })
                : t({ en: "Login", de: "Anmelden" })}
            </button>
          </form>
        </div>

        {/* Registration Link */}
        <div className="text-center space-y-1">
          <p className="text-sm text-gray-600">
            {t({
              en: "Don't have an account yet?",
              de: "Noch kein Konto?",
            })}{" "}
            <Link
              to="/register"
              className="text-[#00A3E0] hover:text-[#002855] font-medium transition-colors"
            >
              {t({ en: "Register here", de: "Hier registrieren" })}
            </Link>
          </p>
          <p className="text-xs text-gray-400">
            {t({
              en: "Already have a burnair Map account? Use the same login here.",
              de: "Bereits ein burnair Map Konto? Verwende dasselbe Login hier.",
            })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
